import React, { Suspense, lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { PATH_AUTH, PATH_DASHBOARD, PATH_OTHER } from "config";
import RouteFallback from "routes/RouteFallback";
import { PublicLayout } from "components/layout/public";
import { PrivateLayout } from "components/layout/private/Private";
import { SettingsShellLayout } from "components/layout/settings/SettingsShellLayout";
import { WorkspaceLayout } from "components/layout/workspace/WorkspaceLayout";
import { ProjectLayoutWrapper } from "components/layout/project/ProjectLayoutWrapper";
import SettingsFallback from "pages/settings/components/SettingsFallback";
import {
  LegacyAccountSettingsRedirect,
  SettingsIndexRedirect,
} from "pages/settings/settingsRedirects";
import { VerifyAccountRedirect } from "components/auth/VerifyAccountRedirect";
import { StripboardProvider } from "components/dashboard/body/projects/panels/schedule/stripboard/StripboardContext";
import { BudgetAiSyncProvider } from "components/dashboard/body/projects/panels/budget/BudgetAiSyncProvider";
import { GenericPanel } from "components/layout/generic";
import {
  ScriptPanel,
  ScriptEditorPanel,
  SnapshotDetailPanel,
  ScriptSnapshotComparePanel,
  OutlinePanel,
  SharedScriptPage,
  NotesPanel,
  TagsPanel,
  ReportPanel,
  CoveragePanel,
  CharacterSummaryPanel,
  CharacterBreakdownPanel,
  Stripboard,
  DayOutOfDays,
  ShootingCalendar,
  RoadmapBoard,
  ActorsManagementPanel,
  TaxIncentives,
  Fringes,
  Actuals,
  Budget,
} from "./panels";
import { LegacyProjectRedirect } from "components/routing/LegacyProjectRedirect";
import { SectionRedirect } from "components/routing/SectionRedirect";
import { ProjectsPage } from "pages/dashboard/projects/ProjectsPage";

const DashboardUpgradePlan = lazy(() =>
  import("pages/dashboard").then((m) => ({ default: m.DashboardUpgradePlan }))
);
const DashboardSelectPlanPage = lazy(() =>
  import("pages/dashboard/plans").then((m) => ({ default: m.DashboardSelectPlanPage }))
);
const PaymentPage = lazy(() =>
  import("pages/dashboard/payment").then((m) => ({ default: m.PaymentPage }))
);
const ActivateAccountPage = lazy(() =>
  import("pages/dashboard/activate").then((m) => ({ default: m.ActivateAccountPage }))
);
const NotFoundPage = lazy(() =>
  import("pages/dashboard/notfound").then((m) => ({ default: m.NotFoundPage }))
);

// Settings pages (lazy with custom fallbacks below in routes)
const ProfileSettingsPage = lazy(() => import("pages/settings/profile/Profile"));
const SecurityAccountPage = lazy(() => import("pages/settings/security/account/Account"));
const SessionsPage = lazy(() => import("pages/settings/security/sessions/Sessions"));
const LoginHistoryPage = lazy(() => import("pages/settings/security/loginHistory/LoginHistory"));
const NotificationsPage = lazy(() => import("pages/settings/notifications/Notifications"));
const BillingSettingsPage = lazy(() => import("pages/settings/billing/Billing"));
const ReferralSettingsPage = lazy(() => import("pages/settings/referral/Referral"));
const WorkspaceSettingsPage = lazy(() => import("pages/settings/workspace/WorkspaceSettings"));
const WorkspaceAccountSettingsPage = lazy(
  () => import("pages/settings/workspace/WorkspaceAccountSettings")
);
const WorkspaceGeneralSettingsPage = lazy(
  () => import("pages/settings/workspace/WorkspaceGeneralSettings")
);
const WorkspaceMembersLayout = lazy(
  () => import("pages/settings/workspace/members/components/WorkspaceMembersLayout")
);
const UsersTabPage = lazy(() => import("pages/settings/workspace/members/pages/UsersTabPage"));
const RolesTabPage = lazy(() => import("pages/settings/workspace/members/pages/RolesTabPage"));
const WorkspaceProjectsPage = lazy(
  () => import("pages/settings/workspace/projects/WorkspaceProjects")
);
const WorkspaceActivityLogPage = lazy(
  () => import("pages/settings/workspace/activity/WorkspaceActivityLog")
);
const WorkspaceBillingPage = lazy(() => import("pages/settings/workspace/WorkspaceBillingPage"));
const WorkspaceConnectionsPage = lazy(
  () => import("pages/settings/workspace/connections/Connections")
);
const WorkspaceSsoPage = lazy(() =>
  import("pages/settings/workspace/sso/WorkspaceSso").then((m) => ({ default: m.WorkspaceSsoPage }))
);
const WorkspaceSettingsSectionGuard = lazy(() =>
  import("pages/settings/workspace/components/WorkspaceSettingsSectionGuard").then((m) => ({
    default: m.WorkspaceSettingsSectionGuard,
  }))
);

const ProjectSettings = lazy(() =>
  import("pages/project/settings/ProjectSettings").then((m) => ({ default: m.ProjectSettings }))
);
const ProjectSettingsGeneralTab = lazy(() =>
  import("pages/project/settings/components/GeneralTab/GeneralTab").then((m) => ({
    default: m.GeneralTab,
  }))
);
const ProjectMembersLayout = lazy(() =>
  import("pages/project/settings/components/ProjectMembersLayout/ProjectMembersLayout").then(
    (m) => ({ default: m.ProjectMembersLayout })
  )
);
const ProjectMembersUsersPage = lazy(() =>
  import("pages/project/settings/components/ProjectMembersUsersPage/ProjectMembersUsersPage").then(
    (m) => ({ default: m.ProjectMembersUsersPage })
  )
);
const ProjectRolesTabPage = lazy(() =>
  import("pages/project/settings/components/ProjectRolesTabPage/ProjectRolesTabPage").then((m) => ({
    default: m.ProjectRolesTabPage,
  }))
);
const ProjectSettingsAiCreditsTab = lazy(() =>
  import("pages/project/settings/components/AiCreditsTab/AiCreditsTab").then((m) => ({
    default: m.AiCreditsTab,
  }))
);
const ProjectSettingsDangerTab = lazy(() =>
  import("pages/project/settings/components/DangerZoneTab/DangerZoneTab").then((m) => ({
    default: m.DangerZoneTab,
  }))
);

// Auth pages (lazy)
const Login = lazy(() => import("pages/auth/login/Login").then((m) => ({ default: m.Login })));
const SignUp = lazy(() => import("pages/auth/signup/SignUp").then((m) => ({ default: m.SignUp })));
const RequestReset = lazy(() =>
  import("pages/auth/resetPassword/RequestReset").then((m) => ({ default: m.RequestReset }))
);
const ConfirmEmail = lazy(() =>
  import("pages/auth/verification/ConfirmEmail").then((m) => ({ default: m.ConfirmEmail }))
);
const ResetPassword = lazy(() =>
  import("pages/auth/resetPassword/ResetPassword").then((m) => ({ default: m.ResetPassword }))
);
const VerifyAccount = lazy(() =>
  import("pages/auth/verification/VerifyAccount").then((m) => ({ default: m.VerifyAccount }))
);
const LoginSso = lazy(() =>
  import("pages/auth/login/LoginSso").then((m) => ({ default: m.LoginSso }))
);
const SsoCallback = lazy(() =>
  import("pages/auth/login/SsoCallback").then((m) => ({ default: m.SsoCallback }))
);
const OAuthCallback = lazy(() =>
  import("pages/auth/login/OAuthCallback").then((m) => ({ default: m.OAuthCallback }))
);
const JoinWorkspace = lazy(() =>
  import("pages/auth/joinWorkspace").then((m) => ({ default: m.JoinWorkspace }))
);
const McpOAuthConsent = lazy(() =>
  import("pages/oauth/consent/McpOAuthConsent").then((m) => ({ default: m.McpOAuthConsent }))
);
const CancelDeletion = lazy(() =>
  import("pages/auth/cancelDeletion/CancelDeletion").then((m) => ({ default: m.CancelDeletion }))
);

export const appRoutes: RouteObject[] = [
  {
    // Outside the public and private layouts: it handles signed-out users itself
    // and must not be redirected to the dashboard when signed in.
    path: PATH_AUTH.mcpOAuthConsent,
    element: (
      <Suspense fallback={<RouteFallback containerClassName="min-h-screen" />}>
        <McpOAuthConsent />
      </Suspense>
    ),
  },
  {
    path: "/s/:token",
    element: (
      <Suspense
        fallback={
          <RouteFallback label="Loading shared script…" containerClassName="min-h-screen" />
        }
      >
        <SharedScriptPage />
      </Suspense>
    ),
  },
  {
    element: <PublicLayout />,
    children: [
      { path: PATH_AUTH.login, element: <Login /> },
      { path: PATH_AUTH.ssoLogin, element: <LoginSso /> },
      { path: PATH_AUTH.ssoCallback, element: <SsoCallback /> },
      { path: PATH_AUTH.oauthCallback, element: <OAuthCallback /> },
      { path: PATH_AUTH.register, element: <SignUp /> },
      { path: PATH_AUTH.signup, element: <SignUp /> },
      { path: PATH_AUTH.resendEmail, element: <ConfirmEmail /> },
      // New cleaner route for email verification
      { path: `${PATH_AUTH.emailVerification}/:status`, element: <VerifyAccount /> },
      // Legacy route redirect - keep for backward compatibility with email links
      { path: `${PATH_AUTH.verifyAccount}/:status`, element: <VerifyAccountRedirect /> },
      { path: `${PATH_AUTH.resetPassword}/:token`, element: <ResetPassword /> },
      { path: PATH_AUTH.forgotPassword, element: <RequestReset /> },
      { path: PATH_AUTH.cancelDeletion, element: <CancelDeletion /> },
      // Legacy accept-invitation route removed; JoinWorkspace handles invites
      { path: PATH_DASHBOARD.joinWorkspace, element: <JoinWorkspace /> },
    ],
  },

  {
    element: <PrivateLayout />,
    children: [
      {
        path: "",
        element: (
          <RouteFallback label="Loading…" containerClassName="h-full min-h-[calc(100svh-54px)]" />
        ),
      },

      {
        path: "/workspaces/:workspaceId/projects",
        element: <ProjectLayoutWrapper />,
        children: [
          {
            index: true,
            element: (
              <Suspense
                fallback={
                  <RouteFallback
                    label="Loading projects…"
                    containerClassName="h-full min-h-[calc(100svh-54px)]"
                  />
                }
              >
                <ProjectsPage />
              </Suspense>
            ),
          },
        ],
      },

      // Projects are created in the sheet. Old links land on the project list instead of
      // matching :projectId below as a project called "new".
      {
        path: "/workspaces/:workspaceId/projects/new",
        element: <Navigate to=".." relative="path" replace />,
      },

      // Workspace-based project routes - MUST come FIRST (more specific)
      {
        path: "/workspaces/:workspaceId/projects/:projectId",
        element: <ProjectLayoutWrapper />,
        children: [
          // Default redirect when visiting workspace project directly
          { index: true, element: <Navigate to="script/breakdown" replace /> },
          {
            path: "script",
            element: (
              <Suspense
                fallback={
                  <RouteFallback
                    label="Loading script…"
                    containerClassName="h-full min-h-[calc(100svh-54px)]"
                  />
                }
              >
                <ScriptPanel />
              </Suspense>
            ),
            children: [
              { index: true, element: <Navigate to="breakdown" replace /> },
              { path: "breakdown", element: <ScriptEditorPanel /> },
              { path: "compare/:snapshotId", element: <ScriptSnapshotComparePanel /> },
              { path: "outline", element: <OutlinePanel /> },
              { path: "snapshots/:snapshotId", element: <SnapshotDetailPanel /> },
              { path: "coverage", element: <CoveragePanel /> },
              { path: "notes", element: <NotesPanel /> },
              { path: "tags", element: <TagsPanel /> },
              { path: "report", element: <ReportPanel /> },
              { path: "*", element: <SectionRedirect to="breakdown" /> },
            ],
          },

          {
            path: "characters",
            element: (
              <Suspense
                fallback={
                  <RouteFallback
                    label="Loading characters…"
                    containerClassName="h-full min-h-[calc(100svh-54px)]"
                  />
                }
              >
                <GenericPanel />
              </Suspense>
            ),
            children: [
              { index: true, element: <Navigate to="breakdown" replace /> },
              { path: "breakdown", element: <CharacterBreakdownPanel /> },
              { path: "summary", element: <CharacterSummaryPanel /> },
              {
                path: "actors",
                element: <ActorsManagementPanel />,
                children: [
                  { index: true, element: <Navigate to="actors" replace /> },
                  { path: ":actorId", element: <ActorsManagementPanel /> },
                ],
              },
              { path: "*", element: <SectionRedirect to="breakdown" /> },
            ],
          },

          {
            path: "schedule",
            element: (
              <Suspense
                fallback={
                  <RouteFallback
                    label="Loading schedule…"
                    containerClassName="h-full min-h-[calc(100svh-54px)]"
                  />
                }
              >
                <StripboardProvider>
                  <GenericPanel />
                </StripboardProvider>
              </Suspense>
            ),
            children: [
              { index: true, element: <Navigate to="stripboard" replace /> },
              { path: "stripboard", element: <Stripboard /> },
              { path: "shootingcalendar", element: <ShootingCalendar /> },
              { path: "roadmap", element: <RoadmapBoard /> },
              { path: "dayoutofdays", element: <DayOutOfDays /> },
              { path: "boneyard", element: <Stripboard isBoneyard /> },
              { path: "*", element: <SectionRedirect to="stripboard" /> },
            ],
          },

          {
            path: "budget",
            element: (
              <Suspense
                fallback={
                  <RouteFallback
                    label="Loading budget…"
                    containerClassName="h-full min-h-[calc(100svh-54px)]"
                  />
                }
              >
                <BudgetAiSyncProvider />
              </Suspense>
            ),
            children: [
              { index: true, element: <Navigate to="breakdown" replace /> },
              { path: "breakdown", element: <Budget /> },
              { path: "taxincentives", element: <TaxIncentives /> },
              { path: "fringes", element: <Fringes /> },
              { path: "actuals", element: <Actuals /> },
              { path: "*", element: <SectionRedirect to="breakdown" /> },
            ],
          },

          {
            path: "settings",
            element: (
              <Suspense
                fallback={
                  <RouteFallback
                    label="Loading project settings…"
                    containerClassName="h-full min-h-[calc(100svh-54px)]"
                  />
                }
              >
                <ProjectSettings />
              </Suspense>
            ),
            children: [
              { index: true, element: <Navigate to="general" replace /> },
              {
                path: "general",
                element: (
                  <Suspense
                    fallback={
                      <RouteFallback
                        label="Loading…"
                        containerClassName="h-full min-h-[calc(100svh-54px)]"
                      />
                    }
                  >
                    <ProjectSettingsGeneralTab />
                  </Suspense>
                ),
              },
              {
                path: "members",
                element: (
                  <Suspense
                    fallback={
                      <RouteFallback
                        label="Loading project members…"
                        containerClassName="h-full min-h-[calc(100svh-54px)]"
                      />
                    }
                  >
                    <ProjectMembersLayout />
                  </Suspense>
                ),
                children: [
                  { index: true, element: <Navigate to="users" replace /> },
                  {
                    path: "users",
                    element: (
                      <Suspense
                        fallback={
                          <RouteFallback
                            label="Loading users…"
                            containerClassName="h-full min-h-[calc(100svh-54px)]"
                          />
                        }
                      >
                        <ProjectMembersUsersPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "roles",
                    element: (
                      <Suspense
                        fallback={
                          <RouteFallback
                            label="Loading roles…"
                            containerClassName="h-full min-h-[calc(100svh-54px)]"
                          />
                        }
                      >
                        <ProjectRolesTabPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "credits",
                element: (
                  <Suspense
                    fallback={
                      <RouteFallback
                        label="Loading…"
                        containerClassName="h-full min-h-[calc(100svh-54px)]"
                      />
                    }
                  >
                    <ProjectSettingsAiCreditsTab />
                  </Suspense>
                ),
              },
              {
                path: "danger",
                element: (
                  <Suspense
                    fallback={
                      <RouteFallback
                        label="Loading…"
                        containerClassName="h-full min-h-[calc(100svh-54px)]"
                      />
                    }
                  >
                    <ProjectSettingsDangerTab />
                  </Suspense>
                ),
              },
              { path: "*", element: <SectionRedirect to="general" /> },
            ],
          },
        ],
      },

      // Workspace settings routes
      {
        path: "/workspaces/:workspaceId/settings",
        element: <WorkspaceLayout />,
        children: [
          {
            element: (
              <Suspense fallback={<SettingsFallback label="Loading workspace settings…" />}>
                <WorkspaceSettingsSectionGuard />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading workspace settings…" />}>
                    <WorkspaceSettingsPage />
                  </Suspense>
                ),
              },
              {
                path: "account",
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading account settings…" />}>
                    <WorkspaceAccountSettingsPage />
                  </Suspense>
                ),
              },
              {
                path: "general",
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading general settings…" />}>
                    <WorkspaceGeneralSettingsPage />
                  </Suspense>
                ),
              },
              {
                path: "members",
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading workspace members…" />}>
                    <WorkspaceMembersLayout />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to="users" replace />,
                  },
                  {
                    path: "users",
                    element: (
                      <Suspense fallback={<SettingsFallback label="Loading users…" />}>
                        <UsersTabPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "roles",
                    element: (
                      <Suspense fallback={<SettingsFallback label="Loading roles…" />}>
                        <RolesTabPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "projects",
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading workspace projects…" />}>
                    <WorkspaceProjectsPage />
                  </Suspense>
                ),
              },
              {
                path: "activity",
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading activity log…" />}>
                    <WorkspaceActivityLogPage />
                  </Suspense>
                ),
              },
              {
                path: "billing",
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading billing…" />}>
                    <WorkspaceBillingPage />
                  </Suspense>
                ),
              },
              {
                path: "connections",
                element: <Navigate to="../api-keys" replace />,
              },
              {
                path: "api-keys",
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading API keys…" />}>
                    <WorkspaceConnectionsPage />
                  </Suspense>
                ),
              },
              {
                path: "sso",
                element: (
                  <Suspense fallback={<SettingsFallback label="Loading SSO settings…" />}>
                    <WorkspaceSsoPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },

      // Legacy redirect for old /projects/:id URLs to workspace-based URLs
      {
        path: "/projects/:projectId/*",
        element: <LegacyProjectRedirect />,
      },

      // Personal settings pages, workspace-independent shell
      {
        element: <SettingsShellLayout />,
        children: [
          // Settings pages with custom loading fallbacks
          { path: "/settings", element: <SettingsIndexRedirect /> },
          {
            path: "/settings/profile",
            element: (
              <Suspense fallback={<SettingsFallback label="Loading profile…" />}>
                <ProfileSettingsPage />
              </Suspense>
            ),
          },
          {
            path: "/settings/security",
            element: <Navigate to="/settings/security/account" replace />,
          },
          {
            path: "/settings/security/account",
            element: (
              <Suspense fallback={<SettingsFallback label="Loading security settings…" />}>
                <SecurityAccountPage />
              </Suspense>
            ),
          },
          {
            path: "/settings/security/sessions",
            element: (
              <Suspense fallback={<SettingsFallback label="Loading active sessions…" />}>
                <SessionsPage />
              </Suspense>
            ),
          },
          {
            path: "/settings/security/login-history",
            element: (
              <Suspense fallback={<SettingsFallback label="Loading login history…" />}>
                <LoginHistoryPage />
              </Suspense>
            ),
          },
          { path: "/settings/account", element: <LegacyAccountSettingsRedirect /> },
          {
            path: "/settings/billing",
            element: (
              <Suspense fallback={<SettingsFallback label="Loading billing…" />}>
                <BillingSettingsPage />
              </Suspense>
            ),
          },
          {
            path: "/settings/referrals",
            element: (
              <Suspense fallback={<SettingsFallback label="Loading referrals…" />}>
                <ReferralSettingsPage />
              </Suspense>
            ),
          },
          {
            path: "/settings/notifications",
            element: (
              <Suspense fallback={<SettingsFallback label="Loading notifications…" />}>
                <NotificationsPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  // Public top-level pages
  { path: PATH_DASHBOARD.upgrade, element: <DashboardUpgradePlan /> },
  { path: PATH_DASHBOARD.selectPlan, element: <DashboardSelectPlanPage /> },
  { path: PATH_DASHBOARD.payments, element: <PaymentPage /> },
  { path: PATH_DASHBOARD.activateAccount, element: <ActivateAccountPage /> },

  // Other pages
  { path: PATH_OTHER.notFound, element: <NotFoundPage /> },

  // Fallback redirect
  { path: "*", element: <NotFoundPage /> },
];

export default appRoutes;
